import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, User } from 'lucide-react';

interface MandatoryProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  missingFields: string[];
}

const MandatoryProfileDialog: React.FC<MandatoryProfileDialogProps> = ({
  open,
  onOpenChange,
  missingFields,
}) => {
  const navigate = useNavigate();

  const handleComplete = () => {
    onOpenChange(false);
    navigate('/profile/edit');
  };

  const getFieldLabel = (field: string): string => {
    switch (field) {
      case 'DOB':
        return 'Date of Birth';
      case 'full_name':
        return 'Full Name';
      case 'location':
        return 'Location';
      case 'postal_code':
        return 'Postal Code';
      default:
        return field;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-warning/20 rounded-full">
              <User className="h-6 w-6 text-warning" />
            </div>
            <AlertDialogTitle className="text-xl">
              Complete Your Profile
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3">
            <p>
              Your name is needed to get started. The rest is optional and just
              helps us personalise matches and rankings — you can add it any time.
            </p>
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-warning text-sm">Not filled in yet:</p>
                  <ul className="text-warning/80 text-sm mt-1 space-y-1">
                    {missingFields.map((field) => (
                      <li key={field}>• {getFieldLabel(field)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction
            onClick={handleComplete}
            className="w-full min-h-[44px] bg-primary hover:bg-primary/90"
          >
            Complete Profile
          </AlertDialogAction>
          <AlertDialogCancel className="w-full min-h-[44px] mt-0">
            Later
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default MandatoryProfileDialog;
